import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { listMyReports, type MyReport } from '../../api/reports';
import { Badge, Card, EmptyState, LoadingState } from '../ui';
import type { BadgeTone } from '../ui';
import './MyReports.css';

const TONES: Record<MyReport['status'], BadgeTone> = {
  OPEN: 'neutral',
  REVIEWING: 'accent',
  RESOLVED: 'success',
  DISMISSED: 'warning',
};

/**
 * What you have told the instance about, and where each one got to.
 *
 * The report control has promised since Phase 17 that "you can see where it got
 * to under your account"; `/me/reports` has answered since then and nothing
 * rendered it. A product that says where something is and then does not show it
 * is worse than one that never offered.
 *
 * What is deliberately **not** here is the moderator's note. A report is not a
 * conversation, and an outcome that arrived with an argument attached would
 * invite one — so this says what was decided, never why.
 */
export function MyReports() {
  const [reports, setReports] = useState<MyReport[] | null>(null);

  useEffect(() => {
    listMyReports()
      .then((page) => setReports(page.results))
      .catch(() => setReports([]));
  }, []);

  if (reports === null) {
    return <LoadingState label="Reading your reports…" />;
  }

  return (
    <Card
      title="What you have reported"
      description="A moderator reads each one and decides. Reporting something never takes it down by itself."
    >
      {reports.length === 0 ? (
        <EmptyState
          title="You have not reported anything"
          description="If something is wrong with a story or a comment, there is a link under it."
        />
      ) : (
        <ul className="sc-my-reports">
          {reports.map((report) => (
            <li key={report.id} className="sc-my-reports__row">
              <div>
                <p className="sc-my-reports__what">
                  <Link to={`/stories/${report.post.id}`}>{report.post.title || 'Untitled'}</Link>
                  {/* Which of your reports this is. The excerpt is the only way
                      to tell four complaints about one story apart a week
                      later. */}
                  {report.comment && (
                    <span className="sc-my-reports__excerpt">
                      {' '}
                      — a comment: “{report.comment.excerpt}”
                    </span>
                  )}
                </p>
                <p className="sc-my-reports__when">
                  {report.kind_label} · {new Date(report.created_at).toLocaleDateString()}
                </p>
              </div>
              <Badge tone={TONES[report.status]} srPrefix="Status">
                {report.status_label}
              </Badge>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
